import { QuestionCircleOutlined } from '@ant-design/icons';
import { Confirm } from '@client/components/_ui/feedback/Confirm';
import { message } from '@client/components/_ui/feedback/message';
import { Trash2 } from 'lucide-react';
import { ButtonIcon } from './ButtonIcon';
import type { DeleteButtonProps } from './types';

/**
 * Standardised delete button with confirmation popover
 *
 * Uses red colour for danger/destructive actions.
 *
 * @example
 * <Button.Delete onConfirm={(onSuccess) => {
 *   deleteItem().then(onSuccess);
 * }} />
 *
 * @example
 * <Button.Delete
 *   onConfirm={handleDelete}
 *   confirmTitle="Delete this record?"
 *   confirmDescription="This action cannot be undone."
 *   confirmOkText="Yes, delete"
 * />
 */
export function ButtonDelete({
    onConfirm,
    disabled,
    loading,
    tooltip = 'Delete',
    size = 'middle',
    confirmTitle = 'Are you sure?',
    confirmDescription,
    confirmOkText = 'Delete',
}: DeleteButtonProps) {
    const handleConfirm = () => {
        message.loading('Deleting...');
        onConfirm(() => message.success('Deleted.'));
    };

    // Tooltip lives on ButtonIcon (which forwards its ref to the real <button> and
    // applies antd's disabled-compat wrapper), not on an outer Tooltip around
    // Confirm. Confirm is a plain function component that neither forwards a ref nor
    // re-emits the hover handlers antd injects, so an outer Tooltip has nothing to
    // attach to and silently never shows.
    const button = (
        <ButtonIcon icon={<Trash2 />} color="red" size={size} disabled={disabled} loading={loading} tooltip={tooltip} />
    );

    return (
        <Confirm
            title={confirmTitle}
            description={confirmDescription}
            icon={<QuestionCircleOutlined className="text-status-danger" />}
            disabled={disabled}
            onOk={handleConfirm}
            okText={confirmOkText}
            okDanger
        >
            {button}
        </Confirm>
    );
}
